import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GroupsRounded,
  SubjectRounded,
  StarRateRounded,
} from '@mui/icons-material';
import i18n from '@src/languages';
import { formatNumberWithSuffix } from '@src/utils/number';
import { COLOR } from '@src/styles/color';
import IconLabel from '@src/new-components/IconLabel';
import { VOICE_ITEM_VARIANT } from '@src/constants/voiceLibrary';
import { VOICE_STATUS } from '@src/constants/voiceCloning';
import { VOICE_LEVEL } from '@src/constants/voice';
import VoiceInfo from '../VoiceInfo';
import {
  StyledVoiceActionContainer,
  StyledVoiceInfoContainer,
  StyledVoiceLanguageContainer,
  StyledVoiceListItem,
  StyledVoiceMetadataContainer,
  StyledVoiceListItemInfoContainer,
} from './index.style';
import VoiceLanguage from '../VoiceLanguage';
import NewVoiceAction from '../NewVoiceAction';

const VoiceListItem = ({
  voice,
  onUseVoice,
  retentionDaysLabel,
  isTrending,
  isVbee,
  section,
  isMyVoice,
  isPlaying,
  onPlayAudio,
  onPauseAudio,
  user,
  isDubbing,
  onUpdateFavouriteVoices,
  isDialog,
  favouriteVoiceCodes,
  isFavourite,
  onFavoriteToggleRequest,
  activeVoiceCode,
  isLeaderboard,
  prizes,
}) => {
  const { t } = useTranslation();

  const [isHover, setIsHover] = useState(false);
  const [parentWidth, setParentWidth] = useState(0);
  const containerRef = useRef(null);
  const isShowButtonUseVoice =
    isHover || parentWidth > 1066 || isVbee || isMyVoice || parentWidth <= 990;
  const isBetween990And1066 = parentWidth >= 990 && parentWidth <= 1066;
  const isGreaterThan1066 = parentWidth > 1066;
  const isLessThan990 = parentWidth < 990;

  const isMyVoiceCloningAboutToRemoved =
    (isMyVoice || isFavourite) && retentionDaysLabel?.isVoiceAboutToBeRemoved;

  const shouldShowRetentionDaysLabel =
    (retentionDaysLabel &&
      !isHover &&
      isBetween990And1066 &&
      !isMyVoice &&
      !isFavourite) ||
    (isGreaterThan1066 && !isMyVoice && !isFavourite) ||
    isMyVoiceCloningAboutToRemoved ||
    (isLessThan990 && retentionDaysLabel?.isVoiceAboutToBeRemoved) ||
    isVbee;
  const { language } = i18n;

  const hiddenCopyLink = voice?.status !== VOICE_STATUS.PUBLIC && isMyVoice;

  // Measure parent container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setParentWidth(width);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const getNumberWithSuffix = (number) =>
    formatNumberWithSuffix(number, language);

  const handleMouseEnter = () => {
    setIsHover(true);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const getRegionName = () => {
    if (voice?.language?.region) {
      return voice?.language?.region[language];
    }
    if (voice?.locale) {
      const regionName = {
        northern: t('localeNorthern'),
        central: t('localeCentral'),
        southern: t('localeSouthern'),
      };
      return regionName[voice?.locale];
    }
    return null;
  };

  const getLanguageName = () => voice?.language?.name[language];

  const handlePlayOrPauseAudio = (e) => {
    if (isPlaying) {
      onPauseAudio(e);
    } else {
      onPlayAudio(e);
    }
  };

  const getGen2Label = () => {
    if (voice?.level === VOICE_LEVEL.GEN2)
      return (
        <IconLabel
          icon={
            <StarRateRounded sx={{ color: COLOR.blueDark, fontSize: 16 }} />
          }
          value={t('GEN2')}
          customStyle={{
            minWidth: '70px',
          }}
          textVariant="body-14-regular"
          textColor={COLOR.blueDark}
        />
      );

    return null;
  };

  return (
    <StyledVoiceListItem
      ref={containerRef}
      isAudioPlaying={isPlaying}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handlePlayOrPauseAudio}
      parentWidth={parentWidth}
      isActive={isDialog && activeVoiceCode === voice?.code}
      isLeaderboard={isLeaderboard}
    >
      <StyledVoiceListItemInfoContainer parentWidth={parentWidth}>
        <StyledVoiceInfoContainer isLeaderboard={isLeaderboard}>
          <VoiceInfo
            voice={voice}
            variant={VOICE_ITEM_VARIANT.LIST}
            isHovered={isHover}
            retentionDaysLabel={retentionDaysLabel}
            isTrending={isTrending}
            isVbee={isVbee}
            section={section}
            isPlaying={isPlaying}
            onPlayAudio={onPlayAudio}
            onPauseAudio={onPauseAudio}
            isLeaderboard={isLeaderboard}
            prizes={prizes}
          />
        </StyledVoiceInfoContainer>
        <StyledVoiceLanguageContainer parentWidth={parentWidth}>
          <VoiceLanguage
            icon={voice?.language?.roundImage}
            language={getLanguageName()}
            region={getRegionName()}
          />
        </StyledVoiceLanguageContainer>
        <StyledVoiceMetadataContainer
          isVoiceAboutToBeRemoved={retentionDaysLabel?.isVoiceAboutToBeRemoved}
          parentWidth={parentWidth}
        >
          {getGen2Label()}
          {(voice?.stats || retentionDaysLabel) && (
            <>
              {!isFavourite &&
                !isVbee &&
                !isMyVoice &&
                (!retentionDaysLabel?.isVoiceAboutToBeRemoved ||
                  parentWidth > 990) && (
                  <>
                    <div className="metadata-item">
                      <IconLabel
                        icon={
                          <GroupsRounded
                            sx={{
                              color: COLOR.indigo[64],
                              width: 16,
                              height: 16,
                            }}
                          />
                        }
                        value={getNumberWithSuffix(
                          voice.stats?.totalUsers ?? 0,
                        )}
                        tooltip={t('totalUsersUsed')}
                        customStyle={{
                          width: 'fit-content',
                        }}
                        textVariant="body-14-regular"
                      />
                    </div>
                    <div className="metadata-item">
                      <IconLabel
                        icon={
                          <SubjectRounded
                            sx={{
                              color: COLOR.indigo[64],
                              width: 16,
                              height: 16,
                            }}
                          />
                        }
                        value={getNumberWithSuffix(
                          voice.stats?.totalCredits ?? 0,
                        )}
                        tooltip={t('totalCharactersConverted')}
                        customStyle={{
                          width: 'fit-content',
                        }}
                        textVariant="body-14-regular"
                      />
                    </div>
                  </>
                )}
              {shouldShowRetentionDaysLabel && (
                <IconLabel
                  icon={retentionDaysLabel?.icon}
                  value={retentionDaysLabel?.title}
                  tooltip={retentionDaysLabel?.contentTooltip}
                  customStyle={{
                    minWidth: '70px',
                  }}
                  textVariant="body-14-regular"
                  isHovered={isHover}
                  parentWidth={parentWidth}
                  textColor={retentionDaysLabel?.textColor}
                  isVoiceAboutToBeRemoved={
                    retentionDaysLabel?.isVoiceAboutToBeRemoved
                  }
                />
              )}
            </>
          )}
        </StyledVoiceMetadataContainer>
      </StyledVoiceListItemInfoContainer>
      <StyledVoiceActionContainer>
        <NewVoiceAction
          user={user}
          voiceCode={voice?.code}
          profilePage={voice?.profilePage}
          onUseVoice={onUseVoice}
          hiddenCopyLink={hiddenCopyLink}
          isHovered={isShowButtonUseVoice}
          onUpdateFavouriteVoices={onUpdateFavouriteVoices}
          favouriteVoiceCodes={favouriteVoiceCodes}
          onFavoriteToggleRequest={onFavoriteToggleRequest}
          isDubbing={isDubbing}
        />
      </StyledVoiceActionContainer>
    </StyledVoiceListItem>
  );
};

export default VoiceListItem;
